import { type_GM_STATE } from '../../helpers'
import { PRICE_ON_APPLICATION_LABEL, isPriceOnApplication } from '../../helpers/formatMoney'
import { PropsWishlistCard, type_WISHLIST_DASHBOARD_STATE } from '../types'
import { LoaderSpin } from './LoaderSpin'

import { HeartIcon } from '../icons/HeartIcon'

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE
declare var Shopify: { currency: { active: string } }

export function WishlistCard({ item, index, removeItem, addToBag, loading, currentVariantId, allowRemoveFromList, currentIndex }: PropsWishlistCard) {
  const isDiscontinued = item?.meta?.discontinued || false
  // destructure handle breaking when handle or templateSuffix was missing.
  // https://www.sarahandsebastian.com/account?view=wishlist#view=detail&id=381242
  // perhaps this is an edge case
  // or old data sitting in this list.
  const handle = item?.meta?.handle
  if (!handle) {
    console.error('item has no handle', item)
  }
  const templateSuffix = item?.meta?.templateSuffix || 'default'
  const productTags = Array.isArray(item?.product?.tags) ? item.product.tags : []
  const normalizedTags = productTags.map((tag) => tag.toLowerCase())
  const hasEngagementStyleTag = normalizedTags.includes('style_engagement')
  const shouldShowPriceOnApplication = isPriceOnApplication(item?.meta?.variantPrice, productTags)
  const shouldUseBookServiceCta = templateSuffix === 'book-a-service'
  const shouldUseClientAdvisorCta = templateSuffix === 'poa-bespoke-enquiry-2' || (templateSuffix === 'default' && shouldShowPriceOnApplication && hasEngagementStyleTag)
  const shouldUseBespokeEnquiryCta = templateSuffix === 'engagement' || (templateSuffix.includes('bespoke-enquiry') && !shouldUseClientAdvisorCta)
  const shouldRedirectToProduct = shouldUseBookServiceCta || shouldUseClientAdvisorCta || shouldUseBespokeEnquiryCta
  const imageUrl = item.product?.images?.[0]?.url || item?.meta?.image?.src || null
  if (!imageUrl) {
    console.error('item has no image', item)
  }
  const pricePersonalised = parseInt(item?.meta?.productPersonalisedPrice) || 0
  const hasVariantPrice = item?.meta?.variantPrice !== null && item?.meta?.variantPrice !== undefined && item?.meta?.variantPrice !== ''

  const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
  const productUrl = item?.product?.external_id || (handle ? `${rootUrlWithoutSlash}/products/${handle}${item?.meta?.variantId ? `?variant=${item.meta.variantId}` : ''}` : '#')
  const isCurrentItemLoading = loading && currentVariantId === item?.meta?.variantId && currentIndex === index

  const handleClick = () => {
    if (shouldRedirectToProduct) {
      location.href = productUrl
    } else {
      addToBag(item, index)
    }
  }

  const handleLabel = () => {
    if (shouldUseBookServiceCta) {
      return wishlistDashboardState.bookService
    }

    if (shouldUseClientAdvisorCta) {
      return wishlistDashboardState.viewProduct
    }

    if (shouldUseBespokeEnquiryCta) {
      return wishlistDashboardState.viewProduct
    }

    if (isDiscontinued) return wishlistDashboardState.discontinued || 'Discountined'

    return item?.meta?.variantAvailable ? wishlistDashboardState.addToBag : wishlistDashboardState.soldOut
  }

  const itemProperties = item?.meta?.properties?.find((prop: any) => parseInt(prop.id) === item?.meta?.variantId)

  const getProperties = (properties: any) => {
    const strArrProperties = []
    for (let key in properties) {
      if (properties.hasOwnProperty(key)) {
        if (
          !key.includes('_') &&
          !key.includes('StockStatus') &&
          !key.includes('pair') &&
          !key.includes('Cartilage') &&
          !key.includes('Engraving Line 1') &&
          properties[key]
        ) {
          const keyValueString = `<span>${key}: ${JSON.stringify(properties[key]).replace('"', '').replace('"', '')}</span>`
          strArrProperties.push(keyValueString)
        }
      }
    }
    return strArrProperties.join('\n')
  }

  const getTitle = () => {
    if (item?.meta?.properties?.some((property: any) => property.hasOwnProperty('pair') && property.pair === 'true')) {
      return `Pair of ${item?.meta?.image?.alt}s`
    }
    else if (item?.meta?.image?.alt) {
      return item?.meta?.image?.alt
    }
    return item?.product?.title
  }

  const getPrice = () => {
    if (shouldShowPriceOnApplication) {
      return PRICE_ON_APPLICATION_LABEL
    }

    let price = Math.round(item?.meta?.variantPrice || 0)
    if (
      item?.meta?.properties?.some((property: any) => property.hasOwnProperty('pair') &&
      property.pair === 'true')
    ) {
      price = price * 2
    }
    else if (
      templateSuffix === 'personalised' &&
      pricePersonalised !== 0 &&
      item?.meta?.properties?.some((item: any) => item.properties.hasOwnProperty('_engravedProduct'))
    ) {
      price = price + pricePersonalised
    }

    return `${(price).toLocaleString()} ${Shopify.currency.active}`
  }

  const getVariantOption = () => {
    if (item?.meta?.properties?.some((property: any) => property.hasOwnProperty('pair') && property.pair === 'true')) {
      return item?.meta?.variantOptions[0]
    } else if(item?.meta?.variantOptions?.length > 1) {
      return item?.meta?.variantOptions?.filter((option: string) => option).join(' / ')
    } else {
      return item?.meta?.productBiline
    }
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="relative aspect-[170/212] group lg:aspect-[335/419]">
        <a href={productUrl} className="w-full h-full">
          {imageUrl && (
            <img
              src={imageUrl}
              className="absolute inset-0 object-cover w-full h-full"
              alt={item?.meta?.image?.alt || item?.meta?.variantSku}
            />
          )}
          {item?.product?.images?.length > 1 && (
            <img
              src={item?.product?.images[1]?.url}
              className="object-cover absolute inset-0 w-full h-full opacity-0 overflow-hidden transition-opacity duration-500 group-hover:!opacity-100 md-max:hidden"
              alt={item?.meta?.image?.alt || item?.meta?.variantSku}
            />
          )}
        </a>
        {allowRemoveFromList && removeItem ? (
          <button className="whishlist_icon absolute top-2.5 right-2.5 z-10 w-4 h-4" onClick={() => removeItem(item)}>
            <HeartIcon className="w-4 h-4 fill-ink text-deep-grey" />
          </button>
        ) : (
          <></>
        )}
      </div>
      <div className="flex items-start justify-between my-2.5 flex-1">
        <div className="flex-1">
          <a href={productUrl} className="mb-0 text-sm-b text-ink font-light capitalize font-bstSpyre">
            {getTitle()}
          </a>
          {hasVariantPrice && <p className="mt-2.5 mb-0 text-xs font-calibre text-ink font-light">{getPrice()}</p>}
          {item?.meta?.variantOptions?.length > 0 && <span className="mt-1.5 text-xs font-calibre font-light text-ink">{getVariantOption()}</span>}
          {itemProperties?.properties && (
            <div
              className="flex flex-col font-light text-ink"
              dangerouslySetInnerHTML={{ __html: getProperties(itemProperties.properties) }}
            ></div>
          )}
        </div>
      </div>
      <button disabled={!shouldRedirectToProduct && item?.meta?.variantAvailable === false || isCurrentItemLoading} className="relative w-full mt-auto mb-[15px] button-tertiary font-calibre lg:mb-5" onClick={() => handleClick()}>
        {isCurrentItemLoading && <LoaderSpin />}
        <span className={isCurrentItemLoading ? 'invisible' : ''}>{handleLabel()}</span>
      </button>
    </div>
  )
}
